import {
    Confirm,
    Deferred,
    Dialog,
    eq,
    Flash,
    Form,
    format,
    GoogleMap,
    Instance,
    Knot,
    Objekt,
    Promize,
    Table,
} from '@siposdani87/sui-js';
import { initTable } from '@utils/collectionSetup';
import { saveAs } from 'file-saver';
import { app } from '../app';
import { EventService } from '@services/eventService';
import { ItemService } from '@services/itemService';
import { LanguageService } from '@services/languageService';
import { MapService } from '@services/mapService';
import { NotificationHistoryService } from '@services/notificationHistoryService';
import { NotificationService } from '@services/notificationService';
import { UserService } from '@services/userService';
import { UtilService } from '@services/utilService';
import { DashboardController } from './dashboardController';

export class EventsShowController extends DashboardController {
    static readonly inject = [
        'instances',
        'mapService',
        'eventService',
        'notificationService',
        'notificationHistoryService',
        'itemService',
        'userService',
        'utilService',
        'languageService',
    ] as const;

    eventId!: string;
    event!: Objekt;
    btnNotificationsExportKnot!: Knot<HTMLElement>;
    btnEventsEditKnot!: Knot<HTMLElement>;
    btnEventsPublishKnot!: Knot<HTMLElement>;
    btnItemsNewKnot!: Knot<HTMLElement>;
    organizationId!: string;
    map!: GoogleMap;
    notificationsTable!: Table;
    itemsTable!: Table;
    private flash: Flash;
    private confirm: Confirm;
    private dialog: Dialog;

    constructor(
        instances: Instance,
        private mapService: MapService,
        private eventService: EventService,
        private notificationService: NotificationService,
        private notificationHistoryService: NotificationHistoryService,
        private itemService: ItemService,
        private userService: UserService,
        private utilService: UtilService,
        private languageService: LanguageService,
    ) {
        super(instances);

        this.flash = instances.flash;
        this.confirm = instances.confirm;
        this.dialog = instances.dialog;
    }

    protected override _initLayout(): void {
        this.eventId = this.state.getParam('eventId');
        this.event = new Objekt();

        this._initToolbarButtons();
        this._initTabPanel('details');
        this._changeToolbarButtons();

        this._initLinks('#details a');
        this._correctDates();

        this._loadEvent().then(() => {
            this._drawContent();
        });
    }

    private _initToolbarButtons(): void {
        this.helper.iconButton('.btn-events-index', this.dom, () => {
            this.state.goBack('events.index');
        });

        this.btnNotificationsExportKnot = this.helper.fabButton(
            '.btn-notifications-export',
            this.dom,
            () => {
                this._exportNotificationsDialog();
            },
        );

        this.btnEventsEditKnot = this.helper.fabButton(
            '.btn-events-edit',
            this.dom,
            () => {
                this.eventService
                    .editDialog(this.event)
                    .then(this.utilService.handleEditResponse('events.index'));
            },
        );

        this.btnEventsPublishKnot = this.helper.fabButton(
            '.btn-events-publish',
            this.dom,
            () => {
                this._publishEvent(this.event);
            },
        );

        this.btnItemsNewKnot = this.helper.fabButton(
            '.btn-items-new',
            this.dom,
            () => {
                this.itemService.newDialogByEvent(this.eventId).then(() => {
                    this.state.refresh(true);
                });
            },
        );
    }

    protected override _drawContent(): Promize | boolean {
        this._changeToolbarButtons();

        this._initMap();
        this._initNotificationsTable();
        this._initItemsTable();
        return true;
    }

    private _changeToolbarButtons(): void {
        this.utilService.showButton(
            this.btnEventsEditKnot,
            this.activeTab === 'details',
        );

        this.utilService.showButton(
            this.btnEventsPublishKnot,
            this.activeTab === 'notifications-table',
        );

        this.utilService.showButton(
            this.btnNotificationsExportKnot,
            this.activeTab === 'notifications-table',
        );

        this.utilService.showButton(
            this.btnItemsNewKnot,
            this.activeTab === 'item-table',
        );
    }

    private _correctDates(): void {
        this.utilService.correctDate('.start-at', 'format.datetime', this.dom);
    }

    private _loadEvent(): Promize {
        const deferred = new Deferred();
        this.eventService
            .get(this.eventId, {
                fields: 'id,title,location',
            })
            .then(
                (response) => {
                    this.event = response.get<Objekt>('event');
                    this.organizationId = this.event.get<string>('organization_id');
                    deferred.resolve();
                },
                () => {
                    deferred.reject();
                },
            );
        return deferred.promise();
    }

    private _initMap(): void {
        if (eq(this.activeTab, 'details')) {
            if (!this.map) {
                this.map = this.mapService.createGoogleMap(this.dom);

                this.map.setMarkerIcon(
                    'location',
                    this.mapService.getIcon('location', '#795548'),
                );
                this.map.on('markerClick', this._eventMarkerClick.bind(this));
            }

            this._setMap();
        }
    }

    private _setMap(): void {
        this.map.removeAllMarker();

        const eventId = this.event.get<string>('id');
        const location = this.event.get<Objekt>('location', new Objekt());
        const latitude = location.get<number>('latitude');
        const longitude = location.get<number>('longitude');

        this.map.createMarker(
            eventId,
            '',
            'location',
            latitude,
            longitude,
            this.event,
        );
        this.map.setCenter(latitude, longitude);
    }

    private _eventMarkerClick(event: Objekt): void {
        const eventId = event.get<string>('id');
        const address = this.event.get<string>('location.address');
        this.map.openInfoWindow(eventId, address);
    }

    private _initNotificationsTable(): void {
        this.notificationsTable = initTable(
            this.notificationsTable,
            this.dom,
            '.notifications-table',
            this.notificationHistoryService,
            {
                entityKey: 'notifications',
                fetchData: (params) =>
                    this.notificationService.getAllByEvent(
                        this.eventId,
                        params,
                    ),
            },
            [
                {
                    style: () => {
                        return [
                            'file_download',
                            this.languageService.translate('buttons.download'),
                        ];
                    },
                    click: (notification) => {
                        const person = notification.get<Objekt>('person');
                        this._exportNotificationsDialog(person).then(() => {
                            this.notificationsTable.refresh();
                        });
                    },
                },
                {
                    style: () => {
                        return [
                            'send',
                            this.languageService.translate('buttons.resend'),
                        ];
                    },
                    click: (notification) => {
                        this._publishNoteByPersonConfirm(notification).then(
                            () => {
                                this.notificationsTable.refresh();
                            },
                        );
                    },
                },
            ],
            'notifications-table',
            this.activeTab,
        )!;
    }

    private _publishNoteByPersonConfirm(notification: Objekt): Promize {
        const deferred = new Deferred();
        this.notificationHistoryService
            .openChooseTypeDialog(
                this.notificationHistoryService.getTypes(notification),
            )
            .then(
                (types) => {
                    this.confirm.load(
                        this.languageService.translate(
                            'events_controller.publish.confirm',
                            {
                                event_title: this.event.get<string>('title'),
                                person_name: notification.get<string>('person.name'),
                            },
                        ),
                        this.languageService.translate('buttons.resend'),
                        this.languageService.translate('buttons.cancel'),
                        '',
                        'warning',
                    );
                    this.confirm.on('ok', () => {
                        const person = notification.get<Objekt>('person');
                        this.eventService
                            .publishByPerson(this.event, person, types)
                            .then(
                                (response) => {
                                    this.flash.addMessage(
                                        response.get('message'),
                                    );
                                    deferred.resolve();
                                },
                                (response) => {
                                    this.flash.addMessage(
                                        response.get('message'),
                                    );
                                    deferred.reject();
                                },
                            );
                    });
                    this.confirm.on('cancel', () => {
                        deferred.reject();
                    });
                    this.confirm.open();
                },
                () => {
                    deferred.reject();
                },
            );
        return deferred.promise();
    }

    private _publishEvent(event: Objekt): void {
        this.notificationHistoryService.openChooseTypeDialog().then((types) => {
            this.eventService.publish(event, types).then(
                (response) => {
                    this.flash.addMessage(response.get('message'));
                    this._drawContent();
                },
                (response) => {
                    this.flash.addMessage(response.get('message'));
                },
            );
        });
    }

    private _initItemsTable(): void {
        this.itemsTable = initTable(
            this.itemsTable,
            this.dom,
            '.items-table',
            this.itemService,
            {
                entityKey: 'items',
                fetchData: (params) =>
                    this.itemService.getAllByEvent(this.eventId, params),
            },
            [
                {
                    style: () => {
                        return [
                            'edit',
                            this.languageService.translate('buttons.edit'),
                            false,
                            !this.userService.hasAccessByOrganization(
                                this.organizationId,
                                'items_write',
                            ),
                        ];
                    },
                    click: (item) => {
                        this.itemService.editDialog(item).then(() => {
                            this.state.refresh(true);
                        });
                    },
                },
            ],
            'item-table',
            this.activeTab,
        )!;
    }

    private _exportNotificationsDialog(opt_person?: Objekt): Promize {
        const deferred = new Deferred();
        this.dialog
            .loadTemplate(
                format('/client/v1/events/{0}/export.html', [this.eventId]),
            )
            .then((dialogKnot) => {
                const form = new Form(dialogKnot);
                form.on('submit', (formData: Objekt) => {
                    const eventData = formData.get<Objekt>('event');
                    const type = eventData.get<string>('export');
                    if (opt_person) {
                        this._exportNotificationsByPerson(
                            this.event,
                            opt_person,
                            type,
                        ).defer(deferred);
                    } else {
                        this._exportNotifications(this.event, type).defer(
                            deferred,
                        );
                    }
                });
                form.on('reset', () => {
                    this.dialog.close();
                    deferred.reject();
                });
                this.dialog.open();
            });
        return deferred.promise();
    }

    private _exportNotifications(event: Objekt, type: string): Promize {
        const deferred = new Deferred();
        this.eventService.exportList(event, type).then(
            (response, filename) => {
                saveAs(response.get<Blob>('raw'), filename);
                this.dialog.close();
                deferred.resolve();
            },
            (response) => {
                this.flash.addMessage(response.get('message'));
                deferred.reject();
            },
        );
        return deferred.promise();
    }

    private _exportNotificationsByPerson(
        event: Objekt,
        person: Objekt,
        type: string,
    ): Promize {
        const deferred = new Deferred();
        this.eventService.exportListByPerson(event, person, type).then(
            (response, filename) => {
                saveAs(response.get<Blob>('raw'), filename);
                this.dialog.close();
                deferred.resolve();
            },
            (response) => {
                this.flash.addMessage(response.get('message'));
                deferred.reject();
            },
        );
        return deferred.promise();
    }

    exit(): void {
        super.exit();

        this.mapService.removeGoogleMap(this.map);
    }
}

export const eventsShowController = app.controller(
    'eventsShowController',
    EventsShowController,
);
